/** One write for the unload path: a path relative to the API base, plus its JSON body. */
export type BeaconRequest = {
  path: string;
  body: unknown;
};

export type SendBeaconRequestsOptions = {
  baseUrl: string;
  token: string | null;
  /** Injectable for tests. Defaults to the page's own fetch. */
  fetchImpl?: typeof fetch;
  /** Called with a request's index in `requests` when its send later rejects. */
  onFailed?: (index: number) => void;
};

/**
 * Combined body budget the browser allows across in-flight `keepalive` requests per
 * origin. Requests past it are not sent — a truncated body is worse than a lost one.
 */
export const KEEPALIVE_BODY_LIMIT_BYTES = 64 * 1024;

const encoder = new TextEncoder();

let keepaliveSupport: boolean | undefined;
let hasWarnedAboutUnsupportedKeepalive = false;

/**
 * Whether this runtime actually honours `keepalive`, probed once and cached. Firefox
 * only shipped it in 133; older ESR channels accept the option and silently ignore it.
 * Probed with an absolute dummy URL, not the empty string some references use — under
 * this repo's jsdom/Node fetch, `new Request('')` throws before the property check ever
 * runs, since there's no document base URL for a relative one to resolve against.
 */
const supportsKeepalive = (): boolean => {
  if (keepaliveSupport === undefined) {
    try {
      keepaliveSupport =
        'keepalive' in new Request('https://keepalive-probe.invalid');
    } catch {
      keepaliveSupport = false;
    }
  }
  return keepaliveSupport;
};

/** Test-only: re-arms the keepalive probe and its warning latch. */
export const resetKeepaliveSupportForTests = (): void => {
  keepaliveSupport = undefined;
  hasWarnedAboutUnsupportedKeepalive = false;
};

/**
 * Sends requests with `keepalive` so they survive page teardown, in order, stopping at
 * the first one that would exceed the budget. Returns how many were sent (issued, not
 * necessarily landed — see `onFailed`) so the caller can put the rest back — stopping
 * rather than skipping keeps writes to the same field from being reordered.
 */
export const sendBeaconRequests = (
  requests: BeaconRequest[],
  options: SendBeaconRequestsOptions,
): number => {
  if (!supportsKeepalive()) {
    // Sending anyway would be an ordinary fetch, killed at teardown just like before
    // this transport existed — but takeUnacknowledged() has already emptied the queue,
    // which would turn that into a silent loss instead of the old, visible one.
    // Returning 0 tells the caller to requeue everything instead.
    if (!hasWarnedAboutUnsupportedKeepalive) {
      hasWarnedAboutUnsupportedKeepalive = true;
      console.error('keepalive unsupported in this runtime; unload save deferred');
    }
    return 0;
  }

  // Bound rather than referenced bare: an unbound `fetch` throws "Illegal invocation".
  const send = options.fetchImpl ?? window.fetch.bind(window);

  let usedBytes = 0;
  let sent = 0;

  for (const [index, request] of requests.entries()) {
    const body = JSON.stringify(request.body ?? null);
    const size = encoder.encode(body).length;

    if (usedBytes + size > KEEPALIVE_BODY_LIMIT_BYTES) break;

    usedBytes += size;
    sent += 1;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (options.token) {
      headers.Authorization = `Bearer ${options.token}`;
    }

    // The response is deliberately never read: during teardown there is nobody left to
    // act on it, which is why this path cannot participate in the ack contract. On a
    // page that survives (bfcache, backgrounded tab) this can still reject, though —
    // onFailed is how the caller learns to put that edit back, since `sent` above
    // already counted it as issued rather than landed.
    void send(`${options.baseUrl}${request.path}`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(error => {
      console.error('Unload save request failed', error);
      options.onFailed?.(index);
    });
  }

  return sent;
};
